import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  AzioneConformita,
  Nis2Asset,
  Nis2Report,
  RequisitoNonMisurato,
} from '../api/types'
import { Donut } from '../components/charts'
import { CatenaFornitura } from '../components/ValutazioniFornitore'
import { DataTable, type Column } from '../components/DataTable'
import {
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  PageSection,
  SectionedPage,
  Spinner,
  StatCard,
} from '../components/common'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { maiuscola, t } from '../i18n/lingua'
import ConformitaReport from './ConformitaReport'
import { EvidenzePiattaforma, FornitoriIct, FunzioniCritiche } from '../components/ConformitaEvidenze'
import { CRITICALITY_LABEL } from './Services'

/**
 * Una pagina di conformità, parametrica sul quadro normativo.
 *
 * NIS2 e DORA sono la stessa pagina con un altro quadro: stessi controlli
 * misurati (il motore è uno), altri articoli citati e un altro elenco di
 * «non misurato». Due pagine copiate divergerebbero al primo ritocco — è la
 * versione in facciata dello stesso difetto che il motore evita sul server.
 *
 * ## La regola che governa la pagina
 *
 * Misurare la parte misurabile e **dichiarare il resto**. Un report di
 * conformità che conta il «non so» come «non serve» — o peggio come
 * «superato» — rassicura fino al giorno dell'audit, dove la differenza salta
 * fuori. Qui ogni controllo ha quattro esiti, e i «non valutabili» dicono come
 * procurarsi il dato: la pagina è una lista di lavoro, non un attestato.
 */

/** I colori delle fasce di conformità. */
const COLORE_FASCIA: Record<string, string> = {
  critico: 'var(--error)',
  parziale: 'var(--warn)',
  da_verificare: 'var(--info)',
  conforme: 'var(--ok)',
}

/** L'etichetta di una fascia, con `t()` letterali (la guardia delle traduzioni
 *  non vede `t(variabile)`). */
function etichettaFascia(fascia: string): string {
  switch (fascia) {
    case 'critico': return t('critico')
    case 'parziale': return t('parziale')
    case 'da_verificare': return t('da verificare')
    case 'conforme': return t('conforme')
    default: return fascia
  }
}

function classeFascia(fascia: string): string {
  switch (fascia) {
    case 'critico':
      return 'badge badge--error'
    case 'parziale':
      return 'badge badge--warn'
    case 'da_verificare':
      return 'badge badge--info'
    default:
      return 'badge badge--ok'
  }
}

/** Il pallino di un controllo nella tabella degli host. */
function Esito({ esito, motivo }: { esito: string; motivo: string }) {
  const simbolo =
    esito === 'ok' ? '●' : esito === 'ko' ? '●' : esito === 'non_valutabile' ? '◐' : '—'
  const colore =
    esito === 'ok'
      ? 'var(--ok)'
      : esito === 'ko'
        ? 'var(--error)'
        : esito === 'non_valutabile'
          ? 'var(--warn)'
          : 'var(--border)'
  const titolo =
    esito === 'ok'
      ? t('Superato')
      : esito === 'ko'
        ? motivo
        : esito === 'non_valutabile'
          ? t('Non valutabile: {motivo}', { motivo })
          : t('Non applicabile')
  return (
    <span title={titolo} style={{ color: colore, cursor: 'help' }} aria-label={titolo}>
      {simbolo}
    </span>
  )
}

/** The outcome of a control as plain text, for filtering, sorting and the CSV. */
function testoEsito(esito: string | undefined): string {
  switch (esito) {
    case 'ok': return t('Superato')
    case 'ko': return t('Fallito')
    case 'non_valutabile': return t('Non valutabile')
    case 'non_applicabile': return t('Non applicabile')
    default: return ''
  }
}

type RequisitoMisurato = Nis2Report['requisiti'][number]
type ServizioConformita = NonNullable<Nis2Report['servizi']>[number]

export interface QuadroConformita {
  framework: 'nis2' | 'dora'
  titolo: string
  sottotitolo: string
  notaNonMisurato: string
  /** `scope`: NIS2 measures the perimeter by default; «tutto» the whole inventory. */
  carica: (scope?: 'perimetro' | 'tutto') => Promise<Nis2Report>
  /** NIS2: what is shown above the measured report (programme at a glance). */
  sintesi?: ReactNode
  /** NIS2: the programme sections (profile and perimeter, exposure, deadlines,
   *  incidents), placed after the overview. `ricarica` reloads the report (a
   *  perimeter change changes what is measured). */
  programma?: (ctx: { ricarica: () => void; scrive: boolean }) => Array<{ id: string; label: string; nodo: ReactNode }>
}

export default function Conformita({ quadro }: { quadro: QuadroConformita }) {
  const [ambito, setAmbito] = useState<'perimetro' | 'tutto'>('perimetro')
  const rapporto = useAsync(() => quadro.carica(ambito), [quadro, ambito])
  const [fascia, setFascia] = useState<string | null>(null)
  const [stampa, setStampa] = useState(false)
  const scrive = usePermessiModuli().scrive(quadro.framework)

  if (rapporto.loading && !rapporto.data) return <Spinner />
  if (rapporto.error) return <ErrorBanner error={rapporto.error} />
  const dati = rapporto.data
  if (!dati) return null

  if (stampa) {
    return (
      <ConformitaReport
        dati={dati}
        titolo={quadro.titolo}
        sottotitolo={quadro.sottotitolo}
        onChiudi={() => setStampa(false)}
      />
    )
  }

  const sezioniProgramma = quadro.programma?.({ ricarica: () => void rapporto.reload(), scrive }) ?? []
  const righe = dati.assets.filter((a) => !fascia || a.fascia === fascia)
  const critici = dati.fasce['critico'] ?? 0
  const daVerificare = dati.fasce['da_verificare'] ?? 0
  const totale = Object.values(dati.fasce).reduce((a, b) => a + b, 0)

  const colonneRequisiti: Column<RequisitoMisurato>[] = [
    { key: 'titolo', header: t('Controllo'), value: (r) => t(r.titolo) },
    { key: 'articolo', header: t('Articolo'), mono: true },
    {
      key: 'esiti',
      header: t('Esiti'),
      value: (r) => t('{ok} ok, {ko} ko, {nv} non valutabili', { ok: r.ok, ko: r.ko, nv: r.non_valutabili }),
      sort: false,
      filter: false,
      render: (r) => {
        const larghezza = (n: number) =>
          `${(100 * n) / Math.max(1, r.ok + r.ko + r.non_valutabili)}%`
        return (
          // The three-colour bar: green passed, red failed, yellow not
          // assessable. Not applicable is not drawn: it is not a gap, it is
          // another question.
          <div
            style={{
              display: 'flex',
              height: 10,
              minWidth: 160,
              borderRadius: 5,
              overflow: 'hidden',
              background: 'var(--muted-bg)',
            }}
            role="img"
            aria-label={t('{ok} ok, {ko} ko, {nv} non valutabili', { ok: r.ok, ko: r.ko, nv: r.non_valutabili })}
          >
            <span style={{ width: larghezza(r.ok), background: 'var(--ok)' }} />
            <span style={{ width: larghezza(r.ko), background: 'var(--error)' }} />
            <span style={{ width: larghezza(r.non_valutabili), background: 'var(--warn)' }} />
          </div>
        )
      },
    },
    {
      key: 'superato',
      header: t('Superato da'),
      align: 'right',
      nowrap: true,
      value: (r) => (r.ok + r.ko > 0 ? `${r.ok}/${r.ok + r.ko}` : t('non misurato')),
      render: (r) => (
        <>
          {r.ok + r.ko > 0 ? `${r.ok}/${r.ok + r.ko}` : t('non misurato')}
          {r.non_valutabili > 0 && <span className="muted"> · {r.non_valutabili} ?</span>}
        </>
      ),
    },
  ]

  const colonneServizi: Column<ServizioConformita>[] = [
    {
      key: 'name',
      header: t('Servizio'),
      value: (s) => `${s.name}${s.owner ? ` ${s.owner}` : ''}`,
      render: (s) => (
        <>
          <Link to={`/services/${s.service_id}`}>{s.name}</Link>
          {s.owner && <><br /><small className="text-muted">{s.owner}</small></>}
        </>
      ),
    },
    { key: 'criticality', header: t('Criticità'), value: (s) => CRITICALITY_LABEL[s.criticality] ?? s.criticality },
    { key: 'components', header: t('Componenti'), type: 'number' },
    { key: 'non_compliant', header: t('Non conformi'), type: 'number' },
    { key: 'infrastructure', header: t('Infrastruttura'), type: 'number' },
  ]

  const colonneHost: Column<Nis2Asset>[] = [
    {
      key: 'fascia',
      header: t('Fascia'),
      value: (a) => maiuscola(etichettaFascia(a.fascia)),
      render: (a) => (
        <span className={classeFascia(a.fascia)}>{maiuscola(etichettaFascia(a.fascia))}</span>
      ),
    },
    {
      key: 'name',
      header: t('Asset'),
      value: (a) => a.name ?? a.ci_id.slice(0, 8),
      render: (a) => <Link to={`/cis/${a.ci_id}`}>{a.name ?? a.ci_id.slice(0, 8)}</Link>,
    },
    {
      key: 'service_criticality',
      header: t('Criticità'),
      value: (a) =>
        a.service_criticality ? (CRITICALITY_LABEL[a.service_criticality] ?? a.service_criticality) : '',
    },
    {
      key: 'punteggio',
      header: t('Punteggio'),
      type: 'number',
      mono: true,
      value: (a) => a.punteggio,
      // The score is on the measured controls only, and when nothing was
      // measured there is NONE: a number over zero measures is not a number.
      render: (a) => (a.punteggio === null ? '—' : `${a.punteggio}%`),
    },
    ...dati.requisiti.map((r): Column<Nis2Asset> => ({
      key: `req:${r.id}`,
      header: (
        <span className="nis2__colonna" title={t(r.titolo)}>
          {t(r.titolo)}
        </span>
      ),
      label: t(r.titolo),
      value: (a) => testoEsito(a.controlli[r.id]?.esito),
      render: (a) => {
        const c = a.controlli[r.id]
        return (
          <span style={{ display: 'block', textAlign: 'center' }}>
            {c ? <Esito esito={c.esito} motivo={c.motivo} /> : '—'}
          </span>
        )
      },
    })),
  ]

  const colonneAzioni: Column<AzioneConformita>[] = [
    {
      key: 'tipo',
      header: t('Tipo'),
      value: (a) => (a.tipo === 'ko' ? t('rimediare') : t('misurare')),
      render: (a) => (
        <span className={a.tipo === 'ko' ? 'badge badge--error' : 'badge badge--warn'}>
          {a.tipo === 'ko' ? t('rimediare') : t('misurare')}
        </span>
      ),
    },
    {
      key: 'titolo',
      header: t('Controllo'),
      value: (a) => `${t(a.titolo)} ${a.articolo}`,
      render: (a) => (
        <>
          {t(a.titolo)} <span className="mono muted">{a.articolo}</span>
        </>
      ),
    },
    { key: 'motivo', header: t('Cosa fare') },
    { key: 'asset_count', header: t('Asset'), type: 'number', mono: true },
    {
      key: 'esempi',
      header: t('Esempi'),
      value: (a) => a.esempi.join(', ') + (a.asset_count > a.esempi.length ? '…' : ''),
      render: (a) => (
        <span className="muted">
          {a.esempi.join(', ')}
          {a.asset_count > a.esempi.length ? '…' : ''}
        </span>
      ),
    },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{quadro.titolo}</h1>
          <p className="page__subtitle">{quadro.sottotitolo}</p>
        </div>
        <div className="toolbar">
          <button type="button" className="secondary" onClick={() => esportaCsv(dati, quadro)}>
            {t('Esporta CSV')}
          </button>
          <button type="button" onClick={() => setStampa(true)}>
            {t('Report da stampare')}
          </button>
        </div>
      </header>

      <SectionedPage
        sections={[
          { id: 'quadro', label: t('Quadro') },
          ...sezioniProgramma.map((x) => ({ id: x.id, label: x.label })),
          { id: 'requisiti', label: t('Requisiti') },
          ...(dati.dora ? [{ id: 'dora', label: t('Funzioni e fornitori') }] : []),
          ...(dati.nis2 ? [{ id: 'fornitori', label: t('Fornitori') }] : []),
          { id: 'host', label: t('Host') },
          ...(dati.evidenze ? [{ id: 'evidenze', label: t('Evidenze') }] : []),
          { id: 'azioni', label: t('Piano di lavoro') },
          { id: 'non-misurato', label: t('Autovalutazione') },
        ]}
      >

      <PageSection id="quadro">
        {quadro.sintesi}
        {dati.perimetro && (
          <p className="field__hint" style={{ marginTop: 0 }}>
            {dati.perimetro.defined ? (
              <>
                {dati.perimetro.scope === 'perimetro'
                  ? t('Controlli misurati sul perimetro NIS2: {n} sistemi.', { n: dati.perimetro.count })
                  : t('Controlli misurati su tutto l’inventario.')}{' '}
                <button type="button" className="link" onClick={() => setAmbito(ambito === 'perimetro' ? 'tutto' : 'perimetro')}>
                  {ambito === 'perimetro' ? t('Mostra tutto l’inventario') : t('Torna al perimetro')}
                </button>
              </>
            ) : (
              <>
                {t('Perimetro NIS2 non definito: i controlli sono misurati su tutto l’inventario.')}{' '}
                <a href="#perimetro">{t('Definisci il perimetro')}</a>
              </>
            )}
          </p>
        )}
        {totale === 0 ? (
          <Card>
            <EmptyState
              title={t('Nessun asset in inventario')}
              hint={t('La conformità si misura su quello che c’è: avvia una discovery.')}
            />
          </Card>
        ) : (
          <div className="row" style={{ alignItems: 'stretch' }}>
            <Card title={t('Conformità del parco')}>
              <Donut
                centreLabel={t('Asset')}
                slices={['critico', 'parziale', 'da_verificare', 'conforme'].map(
                  (f) => ({
                    key: f,
                    label: maiuscola(etichettaFascia(f)),
                    value: dati.fasce[f] ?? 0,
                    color: COLORE_FASCIA[f],
                  }),
                )}
              />
            </Card>
            <div className="stats" style={{ flex: 1, marginBottom: 20 }}>
              <StatCard
                label={t('Asset critici')}
                value={critici}
                tone={critici > 0 ? 'error' : 'ok'}
                hint={t('Vulnerabilità sfruttate attivamente (CISA KEV) o sistemi fuori supporto')}
              />
              <StatCard
                label={t('Da verificare')}
                value={daVerificare}
                tone={daVerificare > 0 ? 'warn' : 'ok'}
                hint={t('Mancano dati per giudicare: agente o controlli sui servizi')}
              />
              <StatCard
                label={t('Generato')}
                value={formatDateTime(dati.generato_al)}
                hint={t('Sui rischi aperti e sull’inventario di adesso')}
              />
            </div>
          </div>
        )}
      </PageSection>

      {sezioniProgramma.map((x) => (
        <PageSection key={x.id} id={x.id}>
          {x.nodo}
        </PageSection>
      ))}

      <PageSection id="requisiti">
        <Card title={t('Requisiti misurati')}>
          <p className="muted" style={{ marginTop: 0 }}>
            {t('Ogni controllo è associato all’articolo della direttiva di riferimento. Le barre mostrano gli asset conformi, non conformi e quelli per cui mancano dati sufficienti alla valutazione.')}
          </p>
          <DataTable
            rows={dati.requisiti}
            columns={colonneRequisiti}
            rowKey={(r) => r.id}
            conteggio={{ nome: t('controlli') }}
          />
        </Card>
      </PageSection>

      {dati.nis2 && (
        <PageSection id="fornitori">
          <CatenaFornitura dati={dati.nis2.catena_fornitura} />
        </PageSection>
      )}

      {dati.dora && (
        <PageSection id="dora">
          <FunzioniCritiche dati={dati.dora.funzioni_critiche} />
          <FornitoriIct dati={dati.dora.fornitori_ict} />
        </PageSection>
      )}

      <PageSection id="host">
        {(dati.servizi?.length ?? 0) > 0 && (
          <Card title={t('Business service ({n})', { n: dati.servizi!.length })}>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('Gli asset a supporto di funzioni critiche o importanti sono identificati tramite i business service. Per ciascun servizio sono indicati il numero di componenti e quelli con conformità critica o parziale. Nella tabella degli host, a parità di fascia di conformità, hanno priorità i componenti dei servizi più critici.')}
            </p>
            <DataTable
              rows={dati.servizi!}
              columns={colonneServizi}
              rowKey={(s) => s.service_id}
              conteggio={{ nome: t('servizi') }}
            />
          </Card>
        )}
        <Card title={t('Host ({n})', { n: dati.assets.length })}>
          <p className="filtro-gravita">
            {['critico', 'parziale', 'da_verificare', 'conforme'].map((f) => {
              const quanti = dati.fasce[f] ?? 0
              if (quanti === 0) return null
              return (
                <button
                  type="button"
                  key={f}
                  className={`chip${fascia === f ? ' chip--attivo' : ''}`}
                  style={{ cursor: 'pointer' }}
                  aria-pressed={fascia === f}
                  onClick={() => setFascia(fascia === f ? null : f)}
                >
                  <span className={classeFascia(f)}>
                    {quanti} {etichettaFascia(f)}
                  </span>
                </button>
              )
            })}
          </p>

          <DataTable
            rows={righe}
            columns={colonneHost}
            rowKey={(a) => a.ci_id}
            conteggio={{ nome: t('host') }}
          />
          <p className="field__hint">
            {t('Passa sopra un pallino per il motivo: ● superato o fallito, ◐ non valutabile (il motivo dice come procurarsi il dato), — non applicabile.')}
          </p>
        </Card>
      </PageSection>

      {dati.evidenze && (
        <PageSection id="evidenze">
          <EvidenzePiattaforma ev={dati.evidenze} framework={quadro.framework} />
        </PageSection>
      )}

      <PageSection id="azioni">
        <Card title={t('Piano di lavoro ({n})', { n: dati.azioni.length })}>
          {dati.azioni.length === 0 ? (
            <p className="muted">{t('Niente da fare: tutti i controlli misurabili passano.')}</p>
          ) : (
            <>
              <p className="muted" style={{ marginTop: 0 }}>
                {t('Azioni derivate dagli esiti dei controlli, raggruppate per causa e ordinate per priorità: prima gli interventi di remediation per i controlli falliti, in seguito le attività di misurazione per i dati mancanti.')}
              </p>
              <DataTable
                rows={dati.azioni}
                columns={colonneAzioni}
                rowKey={(a, i) => `${a.controllo}:${a.tipo}:${i}`}
                conteggio={{ nome: t('azioni') }}
              />
            </>
          )}
        </Card>
      </PageSection>

      <PageSection id="non-misurato">
        <Card title={t('Autovalutazione: cosa questa pagina non misura')}>
          <p style={{ marginTop: 0 }}>{quadro.notaNonMisurato}</p>
          <p className="muted">
            {t('Per ogni requisito, indica come viene coperto dall’organizzazione: stato, riferimento al documento o alla procedura e responsabile. Nel report, queste evidenze sono identificate esplicitamente come dichiarate, non misurate.')}
          </p>
          {dati.non_misurato.map((v) => (
            <Dichiarazione
              key={v.id}
              voce={v}
              framework={quadro.framework}
              onSalvata={() => void rapporto.reload()}
            />
          ))}
        </Card>
      </PageSection>
      </SectionedPage>
    </>
  )
}

// ----------------------------------------- l'autovalutazione, voce per voce

// Etichette con `t()` letterale: la guardia delle traduzioni legge le stringhe
// citate nel sorgente, e `t(variabile)` la renderebbe cieca su queste.
// Una funzione e non una costante: `t()` va chiamata al momento di mostrare,
// non all'import del modulo.
const statiDichiarabili = (): [string, string][] => [
  ['', t('scegli…')],
  ['coperto', t('coperto')],
  ['parziale', t('parzialmente coperto')],
  ['assente', t('non coperto')],
  ['non_applicabile', t('non applicabile')],
]

function Dichiarazione({
  voce,
  framework,
  onSalvata,
}: {
  voce: RequisitoNonMisurato
  framework: string
  onSalvata: () => void
}) {
  const d = voce.declaration
  const [aperta, setAperta] = useState(false)
  const [stato, setStato] = useState(d?.stato ?? '')
  const [riferimento, setRiferimento] = useState(d?.riferimento ?? '')
  const [responsabile, setResponsabile] = useState(d?.responsabile ?? '')
  const [nota, setNota] = useState(d?.nota ?? '')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [salvata, setSalvata] = useState(false)

  async function salva() {
    if (!stato) return
    setBusy(true)
    setErrore(null)
    try {
      await api.dichiaraConformita(framework, voce.id, {
        stato, nota, riferimento, responsabile,
      })
      setSalvata(true)
      onSalvata()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  const badge =
    d?.stato === 'coperto' ? 'badge badge--ok'
    : d?.stato === 'parziale' ? 'badge badge--warn'
    : d?.stato === 'assente' ? 'badge badge--error'
    : d?.stato === 'non_applicabile' ? 'badge'
    : 'badge badge--info'

  return (
    <div className="autoval">
      <button type="button" className="autoval__riga" onClick={() => setAperta(!aperta)}>
        <span className="autoval__caret">{aperta ? '▾' : '▸'}</span>
        <span className="mono autoval__articolo">{voce.articolo}</span>
        <span className="autoval__titolo">{t(voce.titolo)}</span>
        <span className={badge}>
          {d ? (statiDichiarabili().find(([k]) => k === d.stato)?.[1] ?? d.stato) : t('da dichiarare')}
        </span>
      </button>
      {aperta && (
        <div className="autoval__corpo">
          {voce.hint && <p className="field__hint" style={{ marginTop: 0 }}>{t(voce.hint)}</p>}
          {(voce.evidenze?.length ?? 0) > 0 && (
            <p style={{ margin: '0 0 6px' }}>
              <small><strong>{t('Cosa vede la piattaforma')}:</strong> {voce.evidenze!.join(' · ')}</small>
            </p>
          )}
          <div className="autoval__campi">
            <label>
              {t('Stato')}
              <select value={stato} onChange={(e) => setStato(e.target.value)}>
                {statiDichiarabili().map(([k, label]) => (
                  <option key={k} value={k} disabled={k === ''}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {t('Riferimento (documento o procedura)')}
              <input
                value={riferimento}
                onChange={(e) => setRiferimento(e.target.value)}
                placeholder={t('es. Procedura IR-01, registro fornitori, policy MFA')}
                maxLength={400}
              />
            </label>
            <label>
              {t('Responsabile')}
              <input
                value={responsabile}
                onChange={(e) => setResponsabile(e.target.value)}
                placeholder={t('es. CISO, Ufficio compliance')}
                maxLength={200}
              />
            </label>
          </div>
          <label>
            {t('Nota')}
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
              maxLength={4000}
              placeholder={t('Due righe di contesto: come è coperto, da quando, con quali limiti.')}
            />
          </label>
          <div className="toolbar">
            <button type="button" onClick={salva} disabled={busy || !stato}>
              {t('Salva dichiarazione')}
            </button>
            {salvata && <span className="muted">{t('Salvata.')}</span>}
            {d?.updated_at && (
              <span className="muted">
                {t('Ultimo aggiornamento: {quando} ({chi})', {
                  quando: formatDateTime(d.updated_at),
                  chi: d.updated_by || '—',
                })}
              </span>
            )}
          </div>
          <ErrorBanner error={errore} />
        </div>
      )}
    </div>
  )
}

// ------------------------------------------------------- l'export CSV

/**
 * Il CSV degli asset con gli esiti per controllo: la materia prima per chi
 * compila i propri documenti in un foglio di calcolo. Separatore `;` (è ciò
 * che il foglio di calcolo in locale italiano si aspetta), campi con `"`
 * raddoppiate, BOM per l'encoding.
 */
function esportaCsv(dati: Nis2Report, quadro: QuadroConformita) {
  const q = (v: string | number | null | undefined) =>
    `"${String(v ?? '').split('"').join('""')}"`
  const testata = [
    t('Asset'), t('Tipo'), t('Fascia'), t('Criticità ereditata'), t('Punteggio'),
    ...dati.requisiti.map((r) => `${t(r.titolo)} (${r.articolo})`),
    ...dati.requisiti.map((r) => `${t(r.titolo)} — ${t('motivo')}`),
  ]
  const righe = dati.assets.map((a) => [
    q(a.name), q(a.ci_type), q(a.fascia),
    q(a.service_criticality ? (CRITICALITY_LABEL[a.service_criticality] ?? a.service_criticality) : ''),
    q(a.punteggio === null ? '' : `${a.punteggio}%`),
    ...dati.requisiti.map((r) => q(a.controlli[r.id]?.esito ?? '')),
    ...dati.requisiti.map((r) => q(a.controlli[r.id]?.motivo ?? '')),
  ])
  const csv = '\ufeff' + [testata.map(q), ...righe].map((r) => r.join(';')).join('\r\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${quadro.framework}-conformita-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
